import { registerBlock, registerRelationship } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

// --- Main tip block (referenced from semen-analysis AND sperm-preservation) --

const overview: ContentBlock<"tip"> = {
  id: makeId("tip-sperm-optimization"),
  kind: "tip",
  tags: ["sperm", "optimization", "his", "lifestyle", "pre-cycle"],
  title: "His 90-day optimization window",
  subtitle: "The most impactful thing he can do - and it's free",
  icon: "♂",
  body: {
    advice:
      "Avoid hot tubs and saunas, keep laptops off the lap, avoid tight heat-trapping underwear, cut alcohol, sleep 7-8 hours, and exercise moderately. Sperm regenerates every ~72 days - changes now show up in about 3 months.",
    rationale:
      "Unlike eggs, sperm is continuously produced. Every lifestyle change impacts the next generation of sperm being made right now.",
    category: "lifestyle",
  },
  sources: [
    {
      title: "Lifestyle factors and reproductive health",
      journal: "Human Reproduction Update",
      year: 2022,
      evidenceLevel: "meta-analysis",
    },
  ],
};

registerBlock(overview);

// --- Specific interventions ------------------------------------------

const heatAvoidance: ContentBlock<"evidence"> = {
  id: makeId("evidence-sperm-heat"),
  kind: "evidence",
  tags: ["sperm", "optimization", "his", "lifestyle", "evidence-based"],
  title: "Heat exposure and sperm quality",
  body: {
    claim:
      "Avoiding hot tubs, saunas, laptop heat on the lap, and tight heat-trapping underwear can improve sperm parameters within about 3 months.",
    explanation:
      "Testes need to be 2-4°C cooler than body temperature for optimal sperm production. Repeated heat exposure damages developing sperm.",
    strength: "strong",
  },
  sources: [
    {
      title:
        "Effect of increased scrotal temperature on sperm quality and count",
      journal: "Fertility and Sterility",
      year: 2018,
      evidenceLevel: "meta-analysis",
    },
  ],
};

const alcoholReduction: ContentBlock<"evidence"> = {
  id: makeId("evidence-sperm-alcohol"),
  kind: "evidence",
  tags: ["sperm", "optimization", "his", "lifestyle", "evidence-based"],
  title: "Alcohol and sperm quality",
  body: {
    claim:
      "Reducing alcohol to fewer than 5 drinks per week improves sperm volume, count, and morphology.",
    explanation:
      "Heavy drinking lowers testosterone and increases estrogen, directly impairing sperm production. Moderate reduction shows measurable improvement.",
    strength: "strong",
  },
  sources: [
    {
      title: "Alcohol intake and semen quality: a meta-analysis",
      journal: "Andrology",
      year: 2020,
      evidenceLevel: "meta-analysis",
    },
  ],
};

const sleepImpact: ContentBlock<"evidence"> = {
  id: makeId("evidence-sperm-sleep"),
  kind: "evidence",
  tags: ["sperm", "optimization", "his", "lifestyle", "evidence-based"],
  title: "Sleep and sperm health",
  body: {
    claim:
      "7-8 hours of sleep per night is associated with the best semen quality. Both too little and too much sleep are detrimental.",
    explanation:
      "Testosterone peaks during sleep. Disrupted sleep patterns alter hormone rhythms needed for spermatogenesis.",
    strength: "moderate",
  },
};

registerBlock(heatAvoidance);
registerBlock(alcoholReduction);
registerBlock(sleepImpact);

// --- Link interventions to overview ----------------------------------

registerRelationship({
  from: makeId("tip-sperm-optimization"),
  to: makeId("evidence-sperm-heat"),
  type: "deeper-dive",
  label: "Heat exposure details",
});

registerRelationship({
  from: makeId("tip-sperm-optimization"),
  to: makeId("evidence-sperm-alcohol"),
  type: "deeper-dive",
  label: "Alcohol impact details",
});

registerRelationship({
  from: makeId("tip-sperm-optimization"),
  to: makeId("evidence-sperm-sleep"),
  type: "deeper-dive",
  label: "Sleep impact details",
});
